import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import CustomSelect from "components/CustomSelect";
import GoalButton from "layouts/goals/components/GoalButton";
import goalSelectStyles from "layouts/goals/components/goalSelectStyles";
import FieldLabel from "./FieldLabel";
import {
  ASSETS_MESSAGES,
  ASSET_TYPES,
  CURRENCIES,
  DEFAULT_CURRENCY,
  DISPOSAL_REASONS,
  EDITABLE_STATUSES,
  PTA_STATUSES,
  toAmountText,
  typeOf,
} from "../assets.constants";

const toForm = (asset) => ({
  type: asset?.type || "laptop",
  property_of: asset?.property_of || "Productbox",
  name: asset?.name || "",
  serial_number: asset?.serial_number || "",
  status: asset?.status || "active",
  purchased_by: asset?.purchased_by || "",
  purchase_date: asset?.purchase_date || "",
  price: asset?.price ?? "",
  currency: asset?.currency || DEFAULT_CURRENCY,
  accessories: asset?.accessories || "",
  notes: asset?.notes || "",
  specs: { ...(asset?.specs || {}), imeis: undefined },
  imeis: (asset?.specs?.imeis || []).join("\n"),
  disposal_reason: asset?.disposal_reason || "other",
  disposal_date: asset?.disposal_date || "",
  disposed_to: asset?.disposed_to || "",
  disposal_price: asset?.disposal_price ?? "",
});

// IMEIs are typed one per line, but "A & B" or "A, B" from the sheet works too.
const parseImeis = (text) =>
  text
    .split(/[\n,&]+/)
    .map((imei) => imei.trim())
    .filter(Boolean);

function AssetFormModal({ open, onClose, onSave, isSubmitting, asset }) {
  const isEdit = !!asset;
  const [form, setForm] = useState(toForm(null));
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    setForm(toForm(asset));
    setLocalError(null);
  }, [asset, open]);

  const set = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));
  const onInput = (field) => (e) => set(field)(e.target.value);
  const setSpec = (key) => (value) =>
    setForm((prev) => ({ ...prev, specs: { ...prev.specs, [key]: value } }));

  const type = typeOf(form.type);
  const isDisposed = asset?.status === "disposed";

  const typeOptions = ASSET_TYPES.map(({ value, label }) => ({ value, label }));

  const handleSave = async () => {
    if (!form.name.trim()) return setLocalError(ASSETS_MESSAGES.nameRequired);
    if (form.price !== "" && !(Number(form.price) >= 0)) {
      return setLocalError(ASSETS_MESSAGES.priceInvalid);
    }
    setLocalError(null);

    const specs = Object.fromEntries(
      (type.specs || []).map(({ key }) => [key, form.specs[key] || ""])
    );
    if (type.hasPta) specs.pta_status = form.specs.pta_status || "";
    if (type.hasImeis) specs.imeis = parseImeis(form.imeis);

    const body = {
      name: form.name,
      property_of: form.property_of,
      serial_number: form.serial_number,
      purchased_by: form.purchased_by,
      purchase_date: form.purchase_date,
      price: form.price,
      currency: form.currency,
      accessories: form.accessories,
      notes: form.notes,
      specs,
    };
    if (isEdit) {
      if (isDisposed) {
        Object.assign(body, {
          disposal_reason: form.disposal_reason,
          disposal_date: form.disposal_date,
          disposed_to: form.disposed_to,
          disposal_price: form.disposal_price,
        });
      } else {
        body.status = form.status;
      }
    } else {
      Object.assign(body, {
        type: form.type,
      });
    }
    await onSave(body);
  };

  const textField = (field, label, props = {}, width = 6) => (
    <Grid item xs={12} sm={width}>
      <FieldLabel>{label}</FieldLabel>
      <SuiInput
        name={`asset-${field}`}
        autoComplete="off"
        value={form[field]}
        onChange={onInput(field)}
        {...props}
      />
    </Grid>
  );

  const selectField = (label, options, value, onChange, props = {}) => (
    <Grid item xs={12} sm={6}>
      <FieldLabel>{label}</FieldLabel>
      <CustomSelect
        options={options}
        value={options.find((option) => option.value === value) || null}
        onChange={(option) => onChange(option.value)}
        customStyles={goalSelectStyles}
        menuPortalTarget={document.body}
        {...props}
      />
    </Grid>
  );

  return (
    <Dialog open={open} onClose={() => !isSubmitting && onClose()} fullWidth maxWidth="md">
      <DialogTitle>{isEdit ? "Edit Asset" : "Add Asset"}</DialogTitle>
      <DialogContent>
        {localError && (
          <SuiTypography variant="caption" color="error" display="block" mb={1}>
            {localError}
          </SuiTypography>
        )}
        <Grid container spacing={2}>
          {selectField("Type", typeOptions, form.type, set("type"), { isDisabled: isEdit })}
          {textField("name", "Model / name", {
            placeholder: 'e.g. MacBook Air 13.6" 2022 (Space Grey)',
          })}
          {textField("property_of", "Property of", {
            placeholder: "Productbox, or any other owner",
          })}
          {textField("serial_number", type.hasImeis ? "Serial number" : "Serial number / IMEI")}
          {isEdit &&
            !isDisposed &&
            selectField("Status", EDITABLE_STATUSES, form.status, set("status"))}

          {(type.specs || []).map(({ key, label, placeholder }) => (
            <Grid item xs={12} sm={6} key={key}>
              <FieldLabel>{label}</FieldLabel>
              <SuiInput
                name={`asset-spec-${key}`}
                autoComplete="off"
                placeholder={placeholder}
                value={form.specs[key] || ""}
                onChange={(e) => setSpec(key)(e.target.value)}
              />
            </Grid>
          ))}
          {type.hasPta &&
            selectField(
              "PTA status",
              PTA_STATUSES,
              form.specs.pta_status || "",
              setSpec("pta_status")
            )}
          {type.hasImeis &&
            textField(
              "imeis",
              "IMEIs (one per line)",
              { multiline: true, rows: 2, placeholder: "A phone can have two" },
              12
            )}

          {textField("purchased_by", "Purchased by / from", {
            placeholder: "e.g. Luqman Enterprises",
          })}
          {textField("purchase_date", "Purchase date", { type: "date" })}
          {selectField("Currency", CURRENCIES, form.currency, set("currency"))}
          {textField("price", "Price", {
            inputProps: { inputMode: "decimal" },
            onChange: (e) => set("price")(toAmountText(e.target.value)),
            placeholder: "e.g. 237500",
          })}
          {textField(
            "accessories",
            "Accessories",
            { multiline: true, rows: 2, placeholder: "e.g. 35 W charger + USB-C to MagSafe cable" },
            12
          )}
          {textField("notes", "Notes", { multiline: true, rows: 2 }, 12)}
          {isDisposed && (
            <>
              <Grid item xs={12}>
                <SuiTypography variant="button" fontWeight="bold">
                  Disposal
                </SuiTypography>
              </Grid>
              {selectField(
                "Reason",
                DISPOSAL_REASONS,
                form.disposal_reason,
                set("disposal_reason")
              )}
              {textField("disposal_date", "Date", { type: "date" })}
              {textField("disposed_to", "Given / sold to", { placeholder: "Name (free text)" })}
              {textField("disposal_price", `Sale price (${form.currency})`, {
                inputProps: { inputMode: "decimal" },
                onChange: (e) => set("disposal_price")(toAmountText(e.target.value)),
              })}
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <GoalButton variant="secondary" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </GoalButton>
        <GoalButton variant="primary" icon="save" loading={isSubmitting} onClick={handleSave}>
          {isEdit ? "Save changes" : "Add asset"}
        </GoalButton>
      </DialogActions>
    </Dialog>
  );
}

export default AssetFormModal;
